import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  useCreateMtoVedomostRunMutation,
  useListMtoRdDocumentsQuery,
  useListMtoRdProjectsQuery,
} from '@shared/api';
import {
  apiErrorMessage,
  documentMark,
  fmtDateTime,
  markGroup,
  rdIngestLabel,
  rdIngestTone,
} from '@entities/mto';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { IconSearch } from '@shared/ui/icons';
import { matchesSearch, noSubmitOnEnter, selectable, unavailableReason } from '../helpers';
import {
  Check,
  CheckCell,
  Chip,
  Chips,
  Code,
  Err,
  Footer,
  FooterLeft,
  Hint,
  Muted,
  Row,
  SearchWrap,
  Sub,
  Table,
  TableBox,
  Toolbar,
} from '../styles';

export function LibraryMode({
  initialProjectId,
  initialRevisions,
  canWrite,
  onRunCreated,
}: {
  initialProjectId: string;
  initialRevisions: string[];
  canWrite: boolean;
  onRunCreated: (runId: string) => void;
}) {
  const { setActiveProject } = useActiveProjectContext();
  const [projectId, setProjectId] = useState<string>(initialProjectId);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialRevisions));
  const [search, setSearch] = useState('');
  const [markFilter, setMarkFilter] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [poll, setPoll] = useState(0);

  const projectsQ = useListMtoRdProjectsQuery();
  const projects = useMemo(() => projectsQ.data?.items ?? [], [projectsQ.data]);
  const docsQ = useListMtoRdDocumentsQuery(projectId, {
    skip: !projectId,
    pollingInterval: poll,
  });
  // `currentData` — только данные текущего объекта: при смене объекта не
  // показываем на секунду список прежнего.
  const docs = useMemo(() => docsQ.currentData?.items ?? [], [docsQ.currentData]);
  const processing = docs.some((d) => d.ingest_status === 'pending' || d.ingest_status === 'parsing');
  useEffect(() => {
    setPoll(processing ? 15000 : 0);
  }, [processing]);

  const [createVedomost, { isLoading: creatingLib }] = useCreateMtoVedomostRunMutation();

  // Объект по умолчанию — первый действующий с документами.
  useEffect(() => {
    if (projectId || projects.length === 0) return;
    const first = projects.find((p) => p.documents > 0 && p.status === 'active') ?? projects[0];
    if (first) setProjectId(first.id);
  }, [projects, projectId]);

  const groups = useMemo(() => {
    const by = new Map<string, number>();
    for (const d of docs) {
      const g = markGroup(documentMark(d));
      by.set(g, (by.get(g) ?? 0) + 1);
    }
    return [...by.entries()].sort((a, b) => a[0].localeCompare(b[0], 'ru'));
  }, [docs]);

  const visible = useMemo(
    () =>
      docs.filter(
        (d) =>
          matchesSearch(d, search.trim()) &&
          (!markFilter || markGroup(documentMark(d)) === markFilter),
      ),
    [docs, search, markFilter],
  );

  // В ведомость идут только разобранные: выбранные из ссылки, но ещё не
  // разобранные, не считаем.
  const chosen = useMemo(
    () => docs.filter((d) => selected.has(d.revision_id) && selectable(d)),
    [docs, selected],
  );
  const visibleSelectable = visible.filter(selectable);
  const allVisibleSelected =
    visibleSelectable.length > 0 && visibleSelectable.every((d) => selected.has(d.revision_id));

  function toggle(revisionId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(revisionId)) next.delete(revisionId);
      else next.add(revisionId);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleSelectable.forEach((d) => next.delete(d.revision_id));
      else visibleSelectable.forEach((d) => next.add(d.revision_id));
      return next;
    });
  }

  function changeProject(id: string) {
    setProjectId(id);
    const proj = projects.find((p) => p.id === id);
    setActiveProject(id || null, { name: proj?.name, code: proj?.code });
    setSelected(new Set());
    setMarkFilter(null);
    setSearch('');
    setError(null);
  }

  async function submitLibrary(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const ids = chosen.map((d) => d.revision_id);
    if (ids.length === 0) {
      setError('Отметьте документы РД, по которым нужна ведомость.');
      return;
    }
    try {
      const res = await createVedomost({ project_id: projectId, revision_ids: ids }).unwrap();
      onRunCreated(res.run_id);
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось сформировать ведомость. Попробуйте ещё раз.');
    }
  }

  return (
    <>
      <Hint>
        Документы РД загружает и разбирает ПТО. Отметьте нужные — ведомость соберётся из готового
        разбора за несколько секунд: перечень материалов, свод арматуры по классам и диаметрам,
        коды 1С. Если документа нет — загрузите его на вкладке «Свои файлы».
      </Hint>
      <Toolbar>
        <Field>
          <Label htmlFor="mto-rd-project">Объект</Label>
          <Select
            id="mto-rd-project"
            value={projectId}
            onChange={(e) => changeProject(e.target.value)}
            disabled={projectsQ.isLoading || projects.length === 0}
          >
            {projects.length === 0 && <option value="">Объектов нет</option>}
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — документов: {p.documents}
                {p.processing > 0 ? `, в разборе: ${p.processing}` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          <Label htmlFor="mto-rd-search">Поиск по шифру или названию</Label>
          <SearchWrap>
            <IconSearch size={15} />
            <Input
              id="mto-rd-search"
              type="search"
              value={search}
              placeholder="Например: КЖ1 или фундаменты"
              onKeyDown={noSubmitOnEnter}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchWrap>
        </Field>
      </Toolbar>

      {groups.length > 1 && (
        <Chips role="group" aria-label="Фильтр по марке комплекта">
          <Chip
            type="button"
            $active={markFilter === null}
            aria-pressed={markFilter === null}
            onClick={() => setMarkFilter(null)}
          >
            Все марки · {docs.length}
          </Chip>
          {groups.map(([g, n]) => (
            <Chip
              key={g}
              type="button"
              $active={markFilter === g}
              aria-pressed={markFilter === g}
              onClick={() => setMarkFilter(markFilter === g ? null : g)}
            >
              {g} · {n}
            </Chip>
          ))}
        </Chips>
      )}

      <form onSubmit={submitLibrary}>
        {projectsQ.isError ? (
          <Muted role="alert">
            Не удалось загрузить список объектов.{' '}
            <Button type="button" $variant="ghost" $size="sm" onClick={() => projectsQ.refetch()}>
              Повторить
            </Button>
          </Muted>
        ) : docsQ.isError ? (
          <Muted role="alert">
            Не удалось загрузить документы объекта.{' '}
            <Button type="button" $variant="ghost" $size="sm" onClick={() => docsQ.refetch()}>
              Повторить
            </Button>
          </Muted>
        ) : !docsQ.currentData && docsQ.isFetching ? (
          <Muted role="status">Загрузка документов…</Muted>
        ) : docs.length === 0 ? (
          <Muted>
            {projectId
              ? 'В библиотеке этого объекта пока нет документов РД. Их загружает ПТО — или приложите свои файлы.'
              : 'Выберите объект.'}
          </Muted>
        ) : (
          <TableBox tabIndex={0} role="region" aria-label="Документы РД объекта">
            <Table>
              <thead>
                <tr>
                  <th scope="col">
                    <Check
                      type="checkbox"
                      aria-label="Выбрать все показанные документы"
                      checked={allVisibleSelected}
                      disabled={visibleSelectable.length === 0}
                      onChange={toggleAllVisible}
                    />
                  </th>
                  <th scope="col">Марка</th>
                  <th scope="col">Документ</th>
                  <th scope="col" className="num">
                    Листов
                  </th>
                  <th scope="col" className="num">
                    Позиций
                  </th>
                  <th scope="col">Разбор ПТО</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((d) => {
                  const can = selectable(d);
                  const reason = unavailableReason(d);
                  const id = `mto-rd-doc-${d.revision_id}`;
                  const isChosen = can && selected.has(d.revision_id);
                  return (
                    <Row key={d.revision_id} $selected={isChosen}>
                      <CheckCell>
                        <Check
                          id={id}
                          type="checkbox"
                          checked={isChosen}
                          disabled={!can}
                          aria-describedby={reason ? `${id}-why` : undefined}
                          onChange={() => toggle(d.revision_id)}
                        />
                      </CheckCell>
                      <td>
                        <Badge $tone="neutral">{documentMark(d)}</Badge>
                      </td>
                      <td>
                        <label htmlFor={id}>
                          <Code>
                            {d.code}
                            {d.rev_label ? ` · ред. ${d.rev_label}` : ''}
                          </Code>
                          <Sub>{d.title}</Sub>
                          <Sub>
                            {d.source_filename} · загружен {fmtDateTime(d.uploaded_at)}
                          </Sub>
                        </label>
                      </td>
                      <td className="num">{d.total_pages ?? '—'}</td>
                      <td className="num">{d.positions}</td>
                      <td>
                        <Badge $tone={rdIngestTone(d.ingest_status)}>
                          {rdIngestLabel(d.ingest_status)}
                        </Badge>
                        {reason && <Sub id={`${id}-why`}>{reason}</Sub>}
                        {d.ingest_status === 'failed' && d.error_message && (
                          <Sub title={d.error_message}>{d.error_message.slice(0, 160)}</Sub>
                        )}
                      </td>
                    </Row>
                  );
                })}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <Muted>Под фильтр ничего не попало.</Muted>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableBox>
        )}

        {error && <Err role="alert">{error}</Err>}
        <Footer>
          <FooterLeft>
            <span role="status">
              Выбрано документов: <strong>{chosen.length}</strong> из {docs.length}
            </span>
            {chosen.length > 0 && (
              <Button type="button" $variant="ghost" $size="sm" onClick={() => setSelected(new Set())}>
                Снять выбор
              </Button>
            )}
          </FooterLeft>
          <Button
            type="submit"
            $variant="primary"
            disabled={!canWrite || creatingLib || chosen.length === 0}
            title={!canWrite ? 'Только просмотр' : undefined}
          >
            {creatingLib ? 'Формирую…' : `Сформировать ведомость${chosen.length ? ` (${chosen.length})` : ''}`}
          </Button>
        </Footer>
      </form>
    </>
  );
}
